<template>
  <div
    v-if="isSomeWeatherOverlayVisible"
    class="absolute right-8 top-6 flex max-h-[calc(100%-56px)] w-[304px] max-w-[50%] flex-col overflow-auto rounded-lg bg-grey-35"
    :class="{ 'justify-between': !isLegendCollapsed, 'justify-end': isLegendCollapsed }"
  >
    <button class="flex flex-row items-center justify-between px-3 py-2" @click="isLegendCollapsed = !isLegendCollapsed">
      <div class="flex flex-row items-center gap-x-1">
        <gv-icon-sharp name="partly_cloudy_day" size="xs" class="fill-primary" />
        <gv-type uplifted-variant="button">Weather layers legend</gv-type>
      </div>
      <gv-icon :name="isLegendCollapsed ? 'expand_more' : 'expand_less'" size="xs" class="my-1" />
    </button>
    <div v-show="!isLegendCollapsed" class="flex flex-col gap-y-4 bg-white p-2">
      <div v-if="overlayFilters?.[OverlayType.Weather]?.[OverlaySubType.CycloneTrack]" class="flex flex-col gap-2 self-start">
        <div class="text-base font-semibold text-type-primary">Cyclone track</div>
        <img width="289" :src="BomCycloneTrackerLegend" alt="Cyclone track legend" />
      </div>

      <div v-if="overlayFilters?.[OverlayType.Weather]?.[OverlaySubType.RainRadar]" class="flex flex-col gap-2 self-start">
        <div class="text-base font-semibold text-type-primary">Rain radar</div>
        <img width="126" :src="BomRainRadarLegend" alt="Rain radar legend" />
      </div>

      <div v-if="overlayFilters?.[OverlayType.Weather]?.[OverlaySubType.ThunderstormTracker]" class="flex flex-col gap-2 self-start">
        <div class="text-base font-semibold text-type-primary">Thunderstorm tracker</div>
        <div class="flex flex-col gap-1">
          <div class="flex flex-row gap-2">
            <div class="size-4 bg-[#DB0303]"></div>
            <div class="text-xs font-medium text-type-primary">Likely</div>
          </div>
          <div class="flex flex-row gap-2">
            <div class="size-4 bg-[#00B193]"></div>
            <div class="text-xs font-medium text-type-primary">Chance of</div>
          </div>
          <div class="flex flex-row gap-2">
            <div class="size-4 border border-grey-100 bg-[#FFFFFF]"></div>
            <div class="text-xs font-medium text-type-primary">No thunderstorms</div>
          </div>
          <div class="flex flex-row gap-2">
            <div class="size-4 bg-[#D4D4D4]"></div>
            <div class="text-xs font-medium text-type-primary">No data</div>
          </div>
        </div>
      </div>

      <div v-if="overlayFilters?.[OverlayType.Weather]?.[OverlaySubType.WindDirection]" class="flex flex-col gap-2 self-start">
        <div class="text-base font-semibold text-type-primary">Wind direction and speed (knots)</div>
        <div class="flex flex-col gap-1">
          <div class="flex flex-row gap-2">
            <div class="size-4 bg-[#990000]"></div>
            <div class="text-xs font-medium text-type-primary">45+</div>
          </div>
          <div class="flex flex-row gap-2">
            <div class="size-4 bg-[#E4270C]"></div>
            <div class="text-xs font-medium text-type-primary">35 - 45</div>
          </div>
          <div class="flex flex-row gap-2">
            <div class="size-4 bg-[#FF7E4C]"></div>
            <div class="text-xs font-medium text-type-primary">30 - 35</div>
          </div>
          <div class="flex flex-row gap-2">
            <div class="size-4 bg-[#E6E675]"></div>
            <div class="text-xs font-medium text-type-primary">25 - 30</div>
          </div>
          <div class="flex flex-row gap-2">
            <div class="size-4 bg-[#7FDE78]"></div>
            <div class="text-xs font-medium text-type-primary">20 - 25</div>
          </div>
          <div class="flex flex-row gap-2">
            <div class="size-4 bg-[#ACFFA7]"></div>
            <div class="text-xs font-medium text-type-primary">15 - 20</div>
          </div>
          <div class="flex flex-row gap-2">
            <div class="size-4 bg-[#9BDAFD]"></div>
            <div class="text-xs font-medium text-type-primary">10 - 15</div>
          </div>
          <div class="flex flex-row gap-2">
            <div class="size-4 bg-[#CCF0FF]"></div>
            <div class="text-xs font-medium text-type-primary">5 - 10</div>
          </div>
          <div class="flex flex-row gap-2">
            <div class="size-4 border border-grey-100 bg-[#EEF9FD]"></div>
            <div class="text-xs font-medium text-type-primary">0 - 5</div>
          </div>
          <div class="flex flex-row gap-2">
            <div class="size-4 bg-[#D4D4D4]"></div>
            <div class="text-xs font-medium text-type-primary">No data</div>
          </div>
        </div>
      </div>

      <div v-if="overlayFilters?.[OverlayType.Weather]?.[OverlaySubType.FloodCatchments]" class="flex flex-col gap-2 self-start">
        <div class="text-base font-semibold text-type-primary">Flood catchments</div>
        <img width="121" :src="BomFloodCatchmentLegend" alt="Flood catchments legend" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMapStore } from '@/features/hazards';
import { OverlayType, OverlaySubType } from '@/features/hazards/presentation/helpers';
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';

import BomCycloneTrackerLegend from '@/common/assets/images/bom-cyclone-tracker-legend.png';
import BomRainRadarLegend from '@/common/assets/images/bom-rain-radar-legend.png';
import BomFloodCatchmentLegend from '@/common/assets/images/bom-flood-catchment-legend.png';

const map = useMapStore();

const { overlayFilters } = storeToRefs(map);

const OVERLAYS_WITH_LEGENDS = [
  OverlaySubType.CycloneTrack,
  OverlaySubType.RainRadar,
  OverlaySubType.ThunderstormTracker,
  OverlaySubType.WindDirection,
  OverlaySubType.FloodCatchments,
];

const isLegendCollapsed = ref<boolean>(false);

const isSomeWeatherOverlayVisible = computed<boolean>(() => {
  if (!overlayFilters.value?.[OverlayType.Weather]) {
    return false;
  }

  for (const [key, value] of Object.entries(overlayFilters.value[OverlayType.Weather])) {
    if (OVERLAYS_WITH_LEGENDS.includes(key as OverlaySubType) && value) {
      return true;
    }
  }

  return false;
});
</script>
